// @flow

import React, { Fragment } from 'react'
import { CSSTransitionGroup } from 'react-transition-group'
import glamorous from 'glamorous'
import getContent from 'pp-react-l10n'
import { BodyText, grey700 } from '@paypalcorp/pp-react'
import CopyToClipboard from 'react-copy-to-clipboard'
import sharing from 'consumerweb-sharing'

import { svgImages } from 'components/index'
import SVG from 'components/svg-logo'

import { phone5PortraitMaxAndSmaller } from 'lib/styles/media-queries'

const l10n = getContent('pages/shared')
const l10nSettings = getContent('pages/settings')

const ShareTitle = glamorous.div({ color: grey700, textAlign: 'center' })

const SVGParent = glamorous.button({
  textAlign: 'center',
  display: 'inline-block',
  width: '3.75rem',
  margin: '0 0.625rem',
  background: 'none',
  color: 'inherit',
  border: 'none',
  padding: '0',
  font: 'inherit',
  cursor: 'pointer',
  outline: 'inherit',
  transition: 'all .2s ease-in-out',
  ':hover': {
    transform: 'scale(1.1)',
  },
  '@media (max-width: 880px)': {
    transform: 'scale(0.95)',
    marginRight: '10px',
  },
  [phone5PortraitMaxAndSmaller]: {
    transform: 'scale(0.9)',
    marginRight: 0,
  },
  '&:focus': {
    outline: '1px auto rgb(0, 95, 204)',
  },
})

const SVGStyling = glamorous(SVG, {
  forwardProps: ['svgString'],
  rootEl: 'span',
})({})

const SpanText = glamorous.span({
  display: 'inline-table',
  fontSize: '0.8125rem',
  color: '#ababab',
  position: 'relative',
})

const CopyContainer = glamorous.div({
  position: 'relative',
  display: 'inline-block',
})

const TooltipCopied = glamorous.span({
  padding: '0.25rem 0.5rem',
  position: 'absolute',
  bottom: '-2.75rem',
  fontWeight: 500,
  left: '0.5rem',
  backgroundColor: '#e1e7eb',
  color: 'black',

  ':before': {
    content: '""',
    position: 'absolute',
    width: 0,
    height: 0,
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    marginLeft: '5px',
    borderBottom: '6px solid #e1e7eb',
    top: '-6px',
  },

  '&.tooltip-enter': {
    opacity: 0.01,
  },

  '&.tooltip-enter.tooltip-enter-active': {
    opacity: '1',
    transition: 'opacity 300ms ease-in',
  },

  '&.tooltip-leave.tooltip-leave-active': {
    opacity: 0.01,
    transition: 'opacity 300ms ease-in',
  },
})

type State = {
  isCopied: boolean,
}
type Props = {
  slugInfo: { slugDetails: SlugDetails },
  locale?: string,
  country?: string,
  onShare?: (platform: string) => any,
}

class ShareYourLink extends React.Component<Props, State> {
  _ref: ?HTMLElement = null

  state = {
    isCopied: false,
  }

  onShare = platform => {
    if (this.props.onShare) {
      this.props.onShare(platform)
    }
  }

  handleFBClick = (e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault()

    this.onShare('facebook')

    const {
      slugInfo: { slugDetails: { paypalmeSlugName = '' } = {} } = {},
    } = this.props
    sharing.shareURLByFacebook(
      `https://paypal.me/${paypalmeSlugName}?country.x=${this.props.country}&locale.x=${this.props.locale}`,
      575,
      400,
    )
  }

  handleTwitterClick = (e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault()

    this.onShare('twitter')

    const {
      slugInfo: { slugDetails: { paypalmeSlugName = '' } = {} } = {},
    } = this.props
    const twitterShareLink = `${l10n(
      'share.twitter',
    )} https://paypal.me/${encodeURIComponent(paypalmeSlugName)}?country.x=${
      this.props.country
    }&locale.x=${this.props.locale}`
    sharing.shareByTwitter(twitterShareLink, 575, 400)
  }

  handleWhatsappClick = (e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault()

    this.onShare('whatsapp')

    const {
      slugInfo: { slugDetails: { paypalmeSlugName = '' } = {} } = {},
    } = this.props
    sharing.shareByWhatsApp(
      `https://paypal.me/${encodeURIComponent(paypalmeSlugName)}?locale.x=${
        this.props.locale
      }`,
      575,
      400,
    )
  }

  handleMessengerClick = (e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault()

    this.onShare('messenger')

    const {
      slugInfo: { slugDetails: { paypalmeSlugName = '' } = {} } = {},
    } = this.props
    sharing.shareURLByMessenger(
      `https://paypal.me/${encodeURIComponent(paypalmeSlugName)}?country.x=${
        this.props.country
      }&locale.x=${this.props.locale}`,
      '321983581337375',
      window.location.href,
    )
  }

  copyToClipboard() {
    this.onShare('copylink')

    this.setState({ isCopied: true })
    setTimeout(() => {
      this.setState({ isCopied: false })
    }, 1000)
  }

  render() {
    const {
      slugInfo: { slugDetails: { paypalmeSlugName = '' } = {} } = {},
    } = this.props

    return (
      <Fragment>
        <BodyText
          strong
          as={ShareTitle}
          lang={this.props.locale === 'el_GR' ? 'el' : ''}
        >
          {l10n('header.share')}
        </BodyText>
        <div style={{ marginTop: '1rem' }}>
          <CopyToClipboard
            text={`https://paypal.me/${paypalmeSlugName}?country.x=${this.props.country}&locale.x=${this.props.locale}`}
            onCopy={() => {
              this.copyToClipboard()
            }}
          >
            <CopyContainer>
              <SVGParent>
                <SVGStyling svgString={`${svgImages.clipBoard}`} />
                <SpanText>{l10nSettings('share.copy')}</SpanText>
              </SVGParent>
              <CSSTransitionGroup
                transitionName="tooltip"
                transitionEnterTimeout={300}
                transitionLeaveTimeout={300}
              >
                {this.state.isCopied && (
                  <TooltipCopied key="copied-tooltip">
                    <label>{l10n('msg.copied')}</label>
                  </TooltipCopied>
                )}
              </CSSTransitionGroup>
            </CopyContainer>
          </CopyToClipboard>
          <SVGParent onClick={this.handleFBClick}>
            <SVGStyling svgString={`${svgImages.facebook}`} />
            <SpanText>{l10nSettings('share.facebook')}</SpanText>
          </SVGParent>
          <SVGParent onClick={this.handleMessengerClick}>
            <SVGStyling svgString={`${svgImages.messenger}`} />
            <SpanText>{l10nSettings('share.messenger')}</SpanText>
          </SVGParent>
          <SVGParent onClick={this.handleTwitterClick}>
            <SVGStyling svgString={`${svgImages.twitter}`} />
            <SpanText>{l10nSettings('share.twitter')}</SpanText>
          </SVGParent>
        </div>
      </Fragment>
    )
  }
}

export default ShareYourLink
